/**
 * Canonical NZ party configuration.
 * All other party-related lookups are derived from this single source.
 */
export const PARTIES = {
  nat:   { name: "National", shortLabel: "Nat",   color: "#0056A8", field: "nat_pred_n",   pastVoteField: "past_vote_nat"   },
  lab:   { name: "Labour",   shortLabel: "Lab",   color: "#E82C2C", field: "lab_pred_n",   pastVoteField: "past_vote_lab"   },
  act:   { name: "ACT",      shortLabel: "ACT",   color: "#FFD700", field: "act_pred_n",   pastVoteField: "past_vote_act"   },
  green: { name: "Green",    shortLabel: "Green", color: "#009639", field: "green_pred_n", pastVoteField: "past_vote_green" },
  nzf:   { name: "NZF",      shortLabel: "NZF",   color: "#000000", field: "nzf_pred_n",   pastVoteField: "past_vote_nzf"   },
  tpm:   { name: "TPM",      shortLabel: "TPM",   color: "#CC0033", field: "tpm_pred_n",   pastVoteField: "past_vote_tpm"   },
  top:   { name: "TOP",      shortLabel: "TOP",   color: "#008080", field: "top_pred_n",   pastVoteField: ""   },
  oth:   { name: "Other",    shortLabel: "Oth",   color: "#999999", field: "oth_pred_n",   pastVoteField: "past_vote_oth"   },
};

/** Coalition colour — used wherever the right coalition is shown as a single entity */
export const COALITION_COLOR = "#1B4F8A";

/** { "nat_pred_n": { name, color }, ..., coalition: { name, color } } — for map/grid components */
export const PARTY_INFO = {
  ...Object.fromEntries(
    Object.values(PARTIES).map(({ name, color, field }) => [field, { name, color }])
  ),
  coalition: { name: "Coalition", color: COALITION_COLOR },
};

/** { nat: "#0056A8", ... } — for components using short keys */
export const PARTY_COLOURS = Object.fromEntries(
  Object.entries(PARTIES).map(([k, { color }]) => [k, color])
);

/** { National: "#0056A8", ... } — for components using display-name keys */
export const PARTY_COLOURS_BY_NAME = Object.fromEntries(
  Object.values(PARTIES).map(({ name, color }) => [name, color])
);

/** { National: "nat_pred_n", ... } — for VoteLineCharts / NationalVoteBar field lookups */
export const PARTY_FIELDS_BY_NAME = Object.fromEntries(
  Object.values(PARTIES).map(({ name, field }) => [name, field])
);

/** { "nat_pred_n": "past_vote_nat", ... } — for VotersSeats past-vote heat map */
export const PAST_VOTE_MAP = Object.fromEntries(
  Object.values(PARTIES).map(({ field, pastVoteField }) => [field, pastVoteField])
);

/** Ordered list for scatter/arrow comparison controls (excludes Other) */
export const COMPARE_PARTY_OPTIONS = Object.entries(PARTIES)
  .filter(([k]) => k !== "oth")
  .map(([, { shortLabel: label, color, field: key }]) => ({ key, label, color }));

/** Derive { labels, colors } maps from any PARTY_INFO-shaped object */
export function buildLabelsAndColors(info = PARTY_INFO) {
  const labels = {};
  const colors = {};
  Object.entries(info).forEach(([key, { name, color }]) => {
    labels[key] = name;
    colors[key] = color;
  });
  return { labels, colors };
}

/**
 * Long-form labels used by the party pickers on Vote Change and Profiles.
 * Kept separate from PARTIES.name so the dropdowns read as they always have.
 */
export const PARTY_PICKER_LABELS = {
  nat: "National",
  act: "ACT",
  nzf: "NZF",
  lab: "Labour",
  green: "Green",
  tpm: "Te Pāti Māori",
  top: "The Opportunity Party",
};

/**
 * Full party names, for prose and standalone labels ("NZ First Party Vote").
 * PARTIES.name is the compact form (NZF, TPM) and PARTY_PICKER_LABELS is what the
 * dropdowns have always shown — neither reads correctly in a sentence.
 */
export const PARTY_FULL_NAMES = {
  nat: "National",
  lab: "Labour",
  act: "ACT",
  green: "Green",
  nzf: "NZ First",
  tpm: "Te Pāti Māori",
  top: "TOP",
  oth: "Other",
};

/** The order those pickers have always listed parties in. */
export const PARTY_PICKER_ORDER = ["nat", "act", "nzf", "lab", "green", "tpm", "top"];

/**
 * Options for the `*_pred_n` party pickers, narrowed to `visibleSlugs`.
 * "Coalition" is a governing-bloc aggregate, so it is opt-in (internal only).
 * Returns [{ value, label, slug }] — value is the metric field, or "coalition".
 */
export function votePickerOptions(visibleSlugs, { includeCoalition = false } = {}) {
  const allowed = new Set(visibleSlugs || []);
  const options = PARTY_PICKER_ORDER.filter((slug) => allowed.has(slug)).map((slug) => ({
    value: PARTIES[slug].field,
    label: PARTY_PICKER_LABELS[slug],
    fullName: PARTY_FULL_NAMES[slug],
    color: PARTIES[slug].color,
    slug,
  }));
  return includeCoalition
    ? [
        {
          value: "coalition",
          label: "Coalition",
          fullName: "Coalition",
          color: COALITION_COLOR,
          slug: null,
        },
        ...options,
      ]
    : options;
}

/** Short party keys (nat, nzf, …) narrowed to `visibleSlugs`, in canonical order. */
export function visiblePartyKeys(visibleSlugs, order = Object.keys(PARTIES)) {
  const allowed = new Set(visibleSlugs || []);
  return order.filter((slug) => allowed.has(slug));
}
